import { useContext, useEffect, useRef, useState } from "react";
import { useSpring } from "motion/react";
import {
  REFRESH_SPINNER_INITIAL_Y,
  REFRESH_SPINNER_LOADING_Y,
  SCROLL_VIEW_REFETCH_NEGATIVE_Y_POSITION,
  SCROLL_VIEW_REFETCH_Y_POSITION,
} from "common/constants";
import { usePrevious } from "@shared/utils/hooks/misc";
import { SwipeFiltersContext } from "common/Filters/common/SwipeFilters.context.ts";

interface useScrollViewProps {
  onRefresh?: () => void;
  refreshing?: boolean;
  loading?: boolean;
  ignoreExternalRefreshing?: boolean;
}

const useScrollView = ({
  onRefresh,
  refreshing,
  loading,
  ignoreExternalRefreshing,
}: useScrollViewProps) => {
  const { swipeActiveRef } = useContext(SwipeFiltersContext) || {};
  const scrollRef = useRef<any>(null);
  const scrollY = useSpring(0, { stiffness: 800, damping: 100 });
  const startYRef = useRef<number>(0);

  const [showLoading, setShowLoading] = useState(false);
  const [allowRefresh, setAllowRefresh] = useState<boolean>();
  const [allowNegativeScroll, setAllowNegativeScroll] =
    useState<boolean>(false);
  const [hideAnimation, setHideAnimation] = useState(false);
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const prevRefreshing = usePrevious(refreshing);

  useEffect(() => {
    if (!allowRefresh && !!scrollRef.current) {
      setAllowRefresh(scrollRef.current.scrollTop === 0);
    }
  }, []);

  useEffect(() => {
    if (refreshing && !ignoreExternalRefreshing && !loading && !showLoading) {
      setShowLoading?.(true);
      setHideAnimation(true);
    }
  }, [refreshing, ignoreExternalRefreshing, loading, showLoading]);

  useEffect(() => {
    if (showLoading && !refreshing && prevRefreshing) {
      scrollY.set(0);
      setShowLoading?.(false);
      setHideAnimation(false);
      setShouldRefresh(false);
    }
  }, [showLoading, refreshing, prevRefreshing, hideAnimation]);

  const onTouchStart = (e: TouchEvent) => {
    const scrollTop = scrollRef.current?.scrollTop;
    setAllowRefresh(scrollTop > -30 && scrollTop < 10);
    if (!showLoading && !loading) {
      startYRef.current = e.touches[0].pageY;
      scrollY.set(0);
    }
  };

  const onTouchMove = (e: TouchEvent) => {
    if (!allowNegativeScroll && allowRefresh && !showLoading && !loading) {
      const diff = e.touches[0].pageY - startYRef.current;
      const localShouldRefresh = diff > SCROLL_VIEW_REFETCH_Y_POSITION;
      checkShouldRefresh(localShouldRefresh);
      scrollY.set(diff > 0 ? diff : 0);
    }
    if (scrollRef.current !== null && swipeActiveRef?.current) {
      scrollRef.current.style.overflow = "hidden";
    }
  };

  const onTouchEnd = () => {
    if (shouldRefresh) {
      setShowLoading(true);
      const loadingYPosition =
        (allowNegativeScroll ? -1 : 1) *
        (REFRESH_SPINNER_LOADING_Y + Math.abs(REFRESH_SPINNER_INITIAL_Y));
      scrollY.set(loadingYPosition);
      onRefresh?.();
    } else {
      scrollY.set(0);
    }
    if (scrollRef.current !== null) {
      scrollRef.current.style.overflow = "auto";
    }
  };

  const onScroll = () => {
    const scrollTop = scrollRef.current?.scrollTop;
    if (!allowNegativeScroll) {
      setAllowNegativeScroll(scrollTop < 0);
    } else if (scrollTop === 0) {
      setAllowNegativeScroll(false);
    }

    if (allowRefresh && scrollTop < 0 && !showLoading && !loading) {
      const localShouldRefresh =
        scrollTop <= SCROLL_VIEW_REFETCH_NEGATIVE_Y_POSITION;
      checkShouldRefresh(localShouldRefresh);
      scrollY.set(scrollTop);
    }
  };

  const checkShouldRefresh = (localShouldRefresh: boolean) => {
    if (localShouldRefresh && !shouldRefresh) {
      setShouldRefresh(true);
    } else if (!localShouldRefresh && shouldRefresh) {
      setShouldRefresh(false);
    }
  };

  const scrollViewProps = {
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onScroll,
  };

  const spinnerProps = {
    scrollY,
    showLoading,
    allowNegativeScroll,
    hideAnimation,
  };

  const showSpinner = !!onRefresh && (allowRefresh || showLoading || loading);

  return {
    scrollRef,
    scrollViewProps,
    spinnerProps,
    showSpinner,
  };
};

export default useScrollView;
